import { Button, Stack, Typography } from "@mui/material";
import { useUpdateSettlementPoolBoostMutation } from "Api/Mutations/Settlements/useUpdateSettlementPoolBoostMutation";
import { DistributionRatioSlider } from "Components/Settlements/PoolBoost/DistributionRatioSlider";
import { PoolTotalDisplay } from "Components/Settlements/PoolBoost/PoolTotalDisplay";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import {
  getValorBoostFormSchema,
  type ValorBoostFormData,
} from "Components/Settlements/PoolBoost/settlementPoolBoostFormSchema";
import {
  type CurrencyCode,
  LoyaltyPointType,
  type Region,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

const RESOURCES = Resources.Admin.SettlementPoolBoosts;

type Props = {
  region: Region;
  currencyCode: CurrencyCode;
  boostAmount: number;
  hunterPoolRatioPercent: number;
  settlementPoolBoostID: number;
};

export const ValorBoostCard = ({
  region,
  currencyCode,
  boostAmount,
  hunterPoolRatioPercent,
  settlementPoolBoostID,
}: Props) => {
  const { t } = useResource();

  const { mutate: updateBoost, isPending } =
    useUpdateSettlementPoolBoostMutation({
      onSuccess: () => {
        toast.success(t(RESOURCES.SaveSuccess));
      },
    });

  const formValues = { boostAmount, hunterPoolRatioPercent };

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isDirty },
  } = useForm<ValorBoostFormData>({
    resolver: zodResolver(getValorBoostFormSchema(t)),
    defaultValues: formValues,
  });

  const hunterRatio = watch("hunterPoolRatioPercent");

  const onSubmit = ({
    boostAmount,
    hunterPoolRatioPercent,
  }: ValorBoostFormData) => {
    updateBoost({
      id: settlementPoolBoostID,
      data: {
        boostAmount,
        hunterPoolRatioPercent,
        valorPoolRatioPercent: 100 - hunterPoolRatioPercent,
      },
    });
  };

  return (
    <PrimaryPaper>
      <Stack gap={3} component="form" onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h6" fontWeight={600}>
          {t(RESOURCES.ValorBoost)}
        </Typography>

        <PoolTotalDisplay
          region={region}
          loyaltyPointType={LoyaltyPointType.Valor}
          currencyCode={currencyCode}
        />

        <FormInput
          control={control}
          name="boostAmount"
          label={t(RESOURCES.BoostAmount, { currency: currencyCode })}
          isNumber
          min={0}
          disabled={isPending}
        />

        <DistributionRatioSlider
          value={hunterRatio}
          onChange={(value) =>
            setValue("hunterPoolRatioPercent", value, { shouldDirty: true })
          }
          disabled={isPending}
        />

        <Button
          type="submit"
          variant="contained"
          disabled={isPending || !isDirty}
          sx={{ alignSelf: "flex-start" }}
        >
          {t(RESOURCES.SaveButton)}
        </Button>
      </Stack>
    </PrimaryPaper>
  );
};
